import { useEffect } from 'react';
import type { GameZone } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../stores/playtester-store.js';

/**
 * Find which zone a card instance currently lives in.
 * Returns the first zone that contains the instance, or null if not found.
 */
function findZoneForInstance(
  instanceId: string,
  state: ReturnType<typeof usePlaytesterStore.getState>,
): GameZone | null {
  const zones: GameZone[] = ['hand', 'battlefield', 'graveyard', 'exile', 'command', 'library'];
  for (const zone of zones) {
    if (state[zone].some((i) => i.instanceId === instanceId)) return zone;
  }
  return null;
}

/**
 * usePlaytesterKeyboard — registers a global keydown listener while the
 * playtester overlay is active. Skips when an input/textarea/select/contenteditable
 * has focus to avoid interfering with text entry.
 *
 * All keyboard shortcuts are listed in architecture.md §10.
 */
export function usePlaytesterKeyboard(): void {
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const state = usePlaytesterStore.getState();
      if (!state.isActive) return;

      // Skip if user is typing in an input
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        (active instanceof HTMLElement && active.isContentEditable)
      ) {
        return;
      }

      const key = e.key;
      const shift = e.shiftKey;
      const selected = Array.from(state.selectedInstanceIds);

      /**
       * Resolve the effective card IDs for per-card shortcuts.
       * When something is selected, use the selection.
       * When nothing is selected but a card is hovered, use the hovered card.
       */
      function effectiveIds(): string[] {
        if (selected.length > 0) return selected;
        if (state.hoveredInstanceId) return [state.hoveredInstanceId];
        return [];
      }

      // Helper: find the zone of the first effective card
      function effectiveZone(): GameZone | null {
        const ids = effectiveIds();
        if (ids.length === 0) return null;
        return findZoneForInstance(ids[0] ?? '', state);
      }

      switch (key) {
        // ── Life ──
        case 'ArrowUp':
        case '=':
          e.preventDefault();
          state.adjustLife(1);
          break;
        case 'ArrowDown':
        case '-':
          e.preventDefault();
          state.adjustLife(-1);
          break;

        // ── Dice ──
        case '1':
          e.preventDefault();
          state.rollDice('coin');
          break;
        case '2':
          e.preventDefault();
          state.rollDice('d6');
          break;
        case '3':
          e.preventDefault();
          state.rollDice('2d6');
          break;
        case '4':
          e.preventDefault();
          state.rollDice('d20');
          break;

        // ── Library / draw ──
        case 's':
        case 'S':
          e.preventDefault();
          state.shuffleLibrary();
          break;
        case 'd':
        case 'D':
          e.preventDefault();
          state.drawCard();
          break;
        case 'v':
        case 'V':
          e.preventDefault();
          state.setOpenPanel('library');
          break;

        // ── Turn ──
        case 'r':
        case 'R':
          e.preventDefault();
          state.restart();
          break;
        case 'n':
        case 'N': {
          // N is phase-aware: in horde mode it dispatches the correct phase action.
          // Non-horde: next turn (untap + draw).
          e.preventDefault();
          const phase = state.horde?.phase;
          if (!phase) {
            state.nextTurn();
          } else if (phase === 'setup') {
            state.endSetupTurn();
          } else if (phase === 'player-turn') {
            state.endPlayerTurn();
          } else if (phase === 'horde-resolving') {
            state.beginMyTurn();
          }
          break;
        }
        case 'u':
        case 'U':
          // Untap-all is a no-op during horde mode to avoid corrupting horde phase state
          e.preventDefault();
          if (!state.horde) state.untapAll();
          break;

        // ── Zone moves (selection or hovered card fallback) ──
        case 'g':
        case 'G': {
          e.preventDefault();
          const ids = effectiveIds();
          if (ids.length === 0) {
            // Nothing selected or hovered, but the pointer is over the library
            // pile: sending the top card of the library to the graveyard is a
            // mill of one.
            if (state.hoveredZone === 'library') state.millTopX(1);
            break;
          }
          const zone = effectiveZone();
          if (zone) state.moveToZone(ids, zone, 'graveyard');
          break;
        }
        case 'c':
        case 'C': {
          e.preventDefault();
          const ids = effectiveIds();
          if (ids.length === 0) break;
          const zone = effectiveZone();
          if (zone) state.moveToZone(ids, zone, 'command');
          break;
        }
        case 'e':
        case 'E': {
          e.preventDefault();
          const ids = effectiveIds();
          if (ids.length === 0) break;
          const zone = effectiveZone();
          if (zone) state.moveToZone(ids, zone, 'exile');
          break;
        }
        case 'l':
        case 'L': {
          e.preventDefault();
          const ids = effectiveIds();
          if (ids.length === 0) break;
          if (shift) {
            state.moveToBottomOfLibrary(ids);
          } else {
            state.moveToTopOfLibrary(ids);
          }
          break;
        }
        case 'h':
        case 'H': {
          e.preventDefault();
          const ids = effectiveIds();
          if (ids.length === 0) break;
          const zone = effectiveZone();
          if (zone) state.moveToZone(ids, zone, 'hand');
          break;
        }
        case 'b':
        case 'B': {
          e.preventDefault();
          const ids = effectiveIds();
          if (ids.length === 0) break;
          const zone = effectiveZone();
          if (zone) state.moveToZone(ids, zone, 'battlefield');
          break;
        }
        case 't':
        case 'T': {
          if (shift) {
            // Shift+T: toggle click-to-tap mode
            e.preventDefault();
            state.toggleClickToTap();
          } else {
            // T: tap/untap selection or hovered card
            e.preventDefault();
            const ids = effectiveIds();
            if (ids.length > 0) state.tapCards(ids);
          }
          break;
        }
        case 'f':
        case 'F': {
          e.preventDefault();
          const ids = effectiveIds();
          if (ids.length === 0) break;
          for (const id of ids) state.flipCard(id);
          break;
        }
        case 'x':
        case 'X': {
          // Make a token copy of the first effective card
          e.preventDefault();
          const ids = effectiveIds();
          if (ids.length === 0) break;
          // The store owns the cascade placement so X and the context menu agree
          state.makeTokenCopy(ids[0]!);
          break;
        }

        // ── UI toggles ──
        case 'P': {
          if (shift) {
            e.preventDefault();
            // Toggle: if a persistent preview is active, clear it;
            // otherwise pin the currently hovered card (no-op if nothing is hovered).
            if (state.persistentPreviewCard) {
              state.setPersistentPreview(null);
            } else if (state.hoveredCard) {
              state.setPersistentPreview(state.hoveredCard);
            }
          }
          break;
        }

        // ── Dismiss (most-recently-opened first; never exits the game) ──
        case 'Escape':
          e.preventDefault();
          if (state.overlay) {
            // Win/lose overlays: dismiss without ending game
            state.dismissOverlay('continue');
          } else if (state.showCounterModal) {
            state.closeCounterModal();
          } else if (state.showPTModal) {
            state.closePTModal();
          } else if (state.contextMenu) {
            state.setContextMenu(null);
          } else if (state.openPanel) {
            state.setOpenPanel(null);
          } else {
            state.clearSelection();
          }
          break;

        default:
          break;
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // empty deps: store is accessed via getState() inside
}
